import { z } from 'zod';

import { idSchema } from './id';
import { simplePaginationSchema } from './pagination';

export const changeLogEventDetailsTypeSchema = z.union([
    z.literal('archived'),
    z.literal('created'),
    z.literal('deleted'),
    z.literal('edited'),
    z.literal('moved'),
    z.literal('assigned_reviewer'),
    z.literal('unassigned_reviewer'),
    z.literal('reviewed'),
    z.literal('published'),
    z.literal('copied'),
    z.literal('unarchived'),
    z.literal('version_created'),
    z.literal('skills_updated'),
    z.literal('skill_bulk_removed'),
    z.literal('generations_cancelled'),
    z.literal('task_created'),
    z.literal('task_edited'),
    z.literal('task_deleted'),
    z.literal('tasks_reordered'),
    z.literal('task_skills_updated'),
    z.literal('comment_created'),
    z.literal('comment_resolved'),
]);

export type ChangeLogEventDetailsType = z.infer<typeof changeLogEventDetailsTypeSchema>;

export const changeLogEventEntityTypeSchema = z.union([
    z.literal('root'),
    z.literal('family'),
    z.literal('cluster'),
    z.literal('role'),
    z.literal('level'),
]);

export type ChangeLogEventEntityType = z.infer<typeof changeLogEventEntityTypeSchema>;

export const changeLogEventActorSchema = z.object({
    id: z.uuid(),
    emailAddress: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
});

export type ChangeLogEventActor = z.infer<typeof changeLogEventActorSchema>;

const changeLogEntitySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: changeLogEventEntityTypeSchema.optional(),
});

export type ChangeLogEntity = z.infer<typeof changeLogEntitySchema>;

const changeLogEventDetailsBaseSchema = z.object({
    type: changeLogEventDetailsTypeSchema,
});

const changeLogEventArchivedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    type: z.literal('archived'),
});

const changeLogEventCreateDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('created'),
});

const changeLogEventDeleteDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('deleted'),
});

const changeLogEventEditDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    field: z.string(),
    newValue: z.string(),
    oldValue: z.string(),
    type: z.literal('edited'),
});

const changeLogEventMoveDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    from: changeLogEntitySchema,
    to: changeLogEntitySchema,
    type: z.literal('moved'),
});

const changeLogEventAssignReviewerDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('assigned_reviewer'),
});

const changeLogEventUnassignReviewerDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('unassigned_reviewer'),
});

const changeLogEventReviewDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('reviewed'),
    version: z.number().int().positive(),
});

const changeLogEventPublishDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    publisher: changeLogEventActorSchema,
    type: z.literal('published'),
    version: z.number().int().positive(),
});

const changeLogEventCopiedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    from: changeLogEntitySchema,
    to: changeLogEntitySchema,
    type: z.literal('copied'),
});

const changeLogEventUnarchivedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    type: z.literal('unarchived'),
});

const changeLogEventVersionCreatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    type: z.literal('version_created'),
    version: z.number().int().positive(),
});

const changedSkillSchema = z.object({
    id: z.uuid(),
    level: z.number().int().optional(),
    title: z.string(),
});

export type ChangedSkill = z.infer<typeof changedSkillSchema>;

const skillLevelChangeSchema = z.object({
    id: z.uuid(),
    newLevel: z.number().int(),
    oldLevel: z.number().int(),
    title: z.string(),
});

export type SkillLevelChange = z.infer<typeof skillLevelChangeSchema>;

const changeLogEventSkillsUpdatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    skillsAdded: changedSkillSchema.array(),
    skillsLevelChanged: skillLevelChangeSchema.array(),
    skillsRemoved: changedSkillSchema.array(),
    type: z.literal('skills_updated'),
    version: z.number().int().positive().optional(),
});

const bulkRemovalSummarySchema = z.object({
    counts: z.object({
        employees: z.number().int().nonnegative(),
        learningOpportunities: z.number().int().nonnegative(),
        levels: z.number().int().nonnegative(),
        roles: z.number().int().nonnegative(),
        tasks: z
            .number()
            .int()
            .nonnegative()
            .nullish()
            .transform((tasks) => tasks ?? 0),
    }),
    operationId: idSchema,
    // Mirrors the receipt's scopes. Declared here rather than imported from `skill-bulk-removal`, which
    // already imports this module. Nullish so receipts written before selectable scopes still parse.
    scopes: z
        .object({
            employees: z.boolean(),
            learningOpportunities: z.boolean(),
            tasks: z.boolean().nullish(),
        })
        .nullish(),
    skill: z.object({
        id: idSchema,
        title: z.string(),
    }),
});

// Architecture-scoped receipt event: it carries `itemId = null`, so `entity` may be absent.
const changeLogEventSkillBulkRemovedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    bulkRemoval: bulkRemovalSummarySchema,
    entity: changeLogEntitySchema.optional(),
    type: z.literal('skill_bulk_removed'),
});

const changedTaskSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    version: z.number().int().positive(),
});

export type ChangedTask = z.infer<typeof changedTaskSchema>;

// Task events are anchored on the role/level whose profile carries the task; `entity` is that item.
const changeLogEventTaskCreatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    task: changedTaskSchema,
    type: z.literal('task_created'),
    version: z.number().int().positive().optional(),
});

const changeLogEventTaskEditedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    task: changedTaskSchema,
    type: z.literal('task_edited'),
    version: z.number().int().positive().optional(),
});

const changeLogEventTaskDeletedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    task: changedTaskSchema,
    type: z.literal('task_deleted'),
    version: z.number().int().positive().optional(),
});

const changeLogEventTasksReorderedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    tasks: changedTaskSchema.array(),
    type: z.literal('tasks_reordered'),
    version: z.number().int().positive().optional(),
});

const changeLogEventTaskSkillsUpdatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    skillsAdded: changedSkillSchema.array(),
    skillsLevelChanged: skillLevelChangeSchema.array(),
    skillsRemoved: changedSkillSchema.array(),
    task: changedTaskSchema,
    type: z.literal('task_skills_updated'),
    version: z.number().int().positive().optional(),
});

const changedCommentSchema = z.object({
    id: z.uuid(),
    parentId: z.uuid().nullish(),
});

export type ChangedComment = z.infer<typeof changedCommentSchema>;

// Comment events are anchored on the commented role/level; `parentId` is set when the comment is a reply.
const changeLogEventCommentCreatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    comment: changedCommentSchema,
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('comment_created'),
    version: z.number().int().positive().optional(),
});

const changeLogEventCommentResolvedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    comment: changedCommentSchema,
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('comment_resolved'),
    version: z.number().int().positive().optional(),
});

const changeLogEventGenerationsCancelledDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema.optional(),
    type: z.literal('generations_cancelled'),
});

const knownChangeLogEventDetailsSchema = z.discriminatedUnion('type', [
    changeLogEventArchivedDetailSchema,
    changeLogEventCreateDetailSchema,
    changeLogEventDeleteDetailSchema,
    changeLogEventEditDetailSchema,
    changeLogEventMoveDetailSchema,
    changeLogEventAssignReviewerDetailSchema,
    changeLogEventUnassignReviewerDetailSchema,
    changeLogEventReviewDetailSchema,
    changeLogEventPublishDetailSchema,
    changeLogEventCopiedDetailSchema,
    changeLogEventUnarchivedDetailSchema,
    changeLogEventVersionCreatedDetailSchema,
    changeLogEventSkillsUpdatedDetailSchema,
    changeLogEventSkillBulkRemovedDetailSchema,
    changeLogEventGenerationsCancelledDetailSchema,
    changeLogEventTaskCreatedDetailSchema,
    changeLogEventTaskEditedDetailSchema,
    changeLogEventTaskDeletedDetailSchema,
    changeLogEventTasksReorderedDetailSchema,
    changeLogEventTaskSkillsUpdatedDetailSchema,
    changeLogEventCommentCreatedDetailSchema,
    changeLogEventCommentResolvedDetailSchema,
]);

const changeLogEventUnknownDetailSchema = z.looseObject({
    entity: changeLogEntitySchema.optional(),
    type: z.string(),
});

export type ChangeLogEventUnknownDetail = z.infer<typeof changeLogEventUnknownDetailSchema>;

export const changeLogEventDetailsSchema = z.union([
    knownChangeLogEventDetailsSchema,
    changeLogEventUnknownDetailSchema,
]);

export type ChangeLogEventDetails = z.infer<typeof changeLogEventDetailsSchema>;

export type KnownChangeLogEventDetails = z.infer<typeof knownChangeLogEventDetailsSchema>;

export const isKnownChangeLogEventDetail = (details: ChangeLogEventDetails): details is KnownChangeLogEventDetails => {
    return knownChangeLogEventDetailsSchema.safeParse(details).success;
};

export type ChangeLogEventArchivedDetail = z.infer<typeof changeLogEventArchivedDetailSchema>;
export type ChangeLogEventCreateDetail = z.infer<typeof changeLogEventCreateDetailSchema>;
export type ChangeLogEventDeleteDetail = z.infer<typeof changeLogEventDeleteDetailSchema>;
export type ChangeLogEventEditDetail = z.infer<typeof changeLogEventEditDetailSchema>;
export type ChangeLogEventMoveDetail = z.infer<typeof changeLogEventMoveDetailSchema>;
export type ChangeLogEventAssignReviewerDetail = z.infer<typeof changeLogEventAssignReviewerDetailSchema>;
export type ChangeLogEventUnassignReviewerDetail = z.infer<typeof changeLogEventUnassignReviewerDetailSchema>;
export type ChangeLogEventReviewDetail = z.infer<typeof changeLogEventReviewDetailSchema>;
export type ChangeLogEventPublishDetail = z.infer<typeof changeLogEventPublishDetailSchema>;
export type ChangeLogEventCopiedDetail = z.infer<typeof changeLogEventCopiedDetailSchema>;
export type ChangeLogEventUnarchivedDetail = z.infer<typeof changeLogEventUnarchivedDetailSchema>;
export type ChangeLogEventVersionCreatedDetail = z.infer<typeof changeLogEventVersionCreatedDetailSchema>;
export type ChangeLogEventSkillsUpdatedDetail = z.infer<typeof changeLogEventSkillsUpdatedDetailSchema>;
export type ChangeLogEventSkillBulkRemovedDetail = z.infer<typeof changeLogEventSkillBulkRemovedDetailSchema>;
export type ChangeLogEventTaskCreatedDetail = z.infer<typeof changeLogEventTaskCreatedDetailSchema>;
export type ChangeLogEventTaskEditedDetail = z.infer<typeof changeLogEventTaskEditedDetailSchema>;
export type ChangeLogEventTaskDeletedDetail = z.infer<typeof changeLogEventTaskDeletedDetailSchema>;
export type ChangeLogEventTasksReorderedDetail = z.infer<typeof changeLogEventTasksReorderedDetailSchema>;
export type ChangeLogEventTaskSkillsUpdatedDetail = z.infer<typeof changeLogEventTaskSkillsUpdatedDetailSchema>;
export type ChangeLogEventCommentCreatedDetail = z.infer<typeof changeLogEventCommentCreatedDetailSchema>;
export type ChangeLogEventCommentResolvedDetail = z.infer<typeof changeLogEventCommentResolvedDetailSchema>;

export const changeLogEventSchema = z.object({
    actor: changeLogEventActorSchema.optional().nullable(),
    changeId: z.uuid(),
    details: changeLogEventDetailsSchema,
    entityType: changeLogEventEntityTypeSchema,
    eventId: z.uuid(),
    itemId: z.uuid().nullable(),
    timestamp: z.iso.datetime(),
});

export type ChangeLogEvent = z.infer<typeof changeLogEventSchema>;

export const jobArchitectureHistorySchema = changeLogEventSchema.array();

export type JobArchitectureHistory = z.infer<typeof jobArchitectureHistorySchema>;

export const paginatedJobArchitectureHistorySchema = z.object({
    data: changeLogEventSchema.array(),
    pagination: simplePaginationSchema,
});

export type PaginatedJobArchitectureHistory = z.infer<typeof paginatedJobArchitectureHistorySchema>;
